import React, { useRef, useState, useMemo, useEffect } from "react";

const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
import { useDashboard } from "./dashboardStore";

const UserFeatureRequest = () => {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [newItem, setNewItem] = useState({ description: "", priority: "Low" });
  const [errors, setErrors] = useState({});
  const pageSize = 10;
  const [loading, setLoading] = useState(false);


  
  // Subscribe to dashboard store slice
const featureRequestsDat = useDashboard(state => state.data?.featureRequests);

// -------------------------------------
// Process feature requests data
// -------------------------------------
useEffect(() => {
  // Wait until store has data
  if (!featureRequestsDat) return;

  try {
    // Normalize each feature request row if needed
    

    // Save to state
        setItems(featureRequestsDat);
  } catch (err) {
    console.error("Error processing feature requests:", err);
    setItems([]);
  }
}, [featureRequestsDat]); // Reactive: runs whenever store updates


  const validateItem = () => {
    const newErrors = {};
    Object.entries(newItem).forEach(([key, value]) => {
      if (!value) newErrors[key] = true;
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAdd = async (itemToAdd) => {
    if (!validateItem()) return;

    // Automatically add today's date
    const itemWithDate = {
      ...itemToAdd,
      date: new Date().toISOString().split("T")[0],
    };

    try {
      const res = await fetch(`${apiBaseUrl}/api/feature-requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(itemWithDate),
      });

      if (!res.ok) throw new Error("Failed to add item");

      const addedItem = await res.json();
      setItems([addedItem, ...items]);
      setPage(1);
      setShowModal(false);
      setNewItem({ description: "", priority: "Low" });
      setErrors({});
    } catch (err) {
      console.error("Error adding item:", err);
      alert("Failed to add item. Try again.");
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await fetch(`${apiBaseUrl}/api/feature-requests/${id}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
      });
      if (!res.ok) throw new Error("Failed to delete item");
      setItems(items.filter((i) => i.id !== id));
    } catch (err) {
      console.error("Error deleting item:", err);
      alert("Failed to delete item. Try again.");
    }
  };

const filteredItems = useMemo(() => {
  try {
    if (!items || !Array.isArray(items)) return [];

    return [...items] // copy to avoid mutating state
      .sort((a, b) => (b.id ?? 0) - (a.id ?? 0))
      .filter(
        (i) =>
          (i.description?.toLowerCase() ?? "").includes(search.toLowerCase()) ||
          (i.priority?.toLowerCase() ?? "").includes(search.toLowerCase())
      );
  } catch (err) {
    console.error("Error filtering items:", err);
    return [];
  }
}, [items, search]);


  const totalPages = Math.ceil(filteredItems.length / pageSize);
  const currentItems = filteredItems.slice((page - 1) * pageSize, page * pageSize);






  return (
    <div className="w-full flex-1 overflow-y-auto p-4">
      {/* Search + Add */}
      <div className="flex flex-col sm:flex-row items-center justify-between mb-4 gap-2">
        <input
          type="text"
          placeholder="Search by description or priority..."
          className="px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring focus:ring-blue-200 flex-1"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />
        <button
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 cursor-pointer"
          onClick={() => setShowModal(true)}
        >
          Add Item
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-md border border-gray-200">
        <table className="min-w-full divide-y divide-gray-100 text-sm md:text-base">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-4 text-left font-medium text-gray-700 w-12">#</th>
              <th className="px-3 py-4 text-left font-medium text-gray-700">Description</th>
              <th className="px-3 py-4 text-left font-medium text-gray-700">Priority</th>
              <th className="px-3 py-4 text-left font-medium text-gray-700">Date</th>
              <th className="px-3 py-4 text-center font-medium text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {currentItems.length === 0 && (
              <tr>
                <td colSpan={5} className="px-3 py-4 text-center text-gray-500 italic">
                  
                  
                  {loading ? (
                    <p className="text-center text-blue-400 italic">Loading...</p>
                  ) : (
                    <p className="text-center text-gray-500 italic">No requests found</p>
                  )}
                  
                </td>
              </tr>
            )}
            {currentItems.map((item, idx) => (
              <tr key={item.id} className="hover:bg-gray-300 ">
                <td className="px-3 py-2 text-gray-600">{idx + 1 + (page - 1) * pageSize}</td>
                
            <td className="px-3 py-2 font-medium text-gray-800 break-words whitespace-normal max-w-[40ch]">
              {item.description}
            </td>


                <td className="px-3 py-2 text-gray-600">{item.priority}</td>
                
                <td className="px-3 py-2 text-gray-600">
                
                  {new Date(item.date).toLocaleDateString("en-GB", {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      })}
                
                </td>
                
                <td className="px-3 py-2 text-center">
                  <button
                    className="px-2 py-1 text-sm text-white bg-red-600 rounded-md hover:bg-red-700 cursor-pointer"
                    onClick={() => handleDelete(item.id)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-3">
          <button
            className="px-3 py-1 text-sm rounded-md border disabled:opacity-50 cursor-pointer"
            onClick={() => setPage((p) => Math.max(p - 1, 1))}
            disabled={page === 1}
          >
            Prev
          </button>
          <span className="text-sm text-gray-700">
            Page {page} of {totalPages}
          </span>
          <button
            className="px-3 py-1 text-sm rounded-md border disabled:opacity-50 cursor-pointer"
            onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
            disabled={page === totalPages}
          >
            Next
          </button>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white w-full max-w-md rounded-md shadow-lg p-6 relative">
            <h2 className="text-lg font-semibold mb-4">Add New Feature Request</h2>
            <div className="flex flex-col gap-3">
              {/* Description */}
              <input
                type="text"
                placeholder="Description"
                className={`px-3 py-2 border rounded-md focus:outline-none focus:ring focus:ring-blue-200 ${
                  errors.description ? "border-red-500" : "border-gray-300"
                }`}
                value={newItem.description}
                onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
              />

              {/* Priority dropdown */}
              <select
                className={`px-3 py-2 border rounded-md focus:outline-none focus:ring focus:ring-blue-200 ${
                  errors.priority ? "border-red-500" : "border-gray-300"
                }`}
                value={newItem.priority}
                onChange={(e) => setNewItem({ ...newItem, priority: e.target.value })}
              >
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
              </select>
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <button
                className="px-4 py-2 rounded-md border hover:bg-gray-100 cursor-pointer"
                onClick={() => {
                  setShowModal(false);
                  setErrors({});
                }}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 cursor-pointer"
                onClick={() => handleAdd(newItem)}
              >
                Add
              </button>
            </div>
            <button
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 cursor-pointer"
              onClick={() => {
                setShowModal(false);
                setErrors({});
              }}
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserFeatureRequest;
